/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, inject, Input, input, OnInit, Optional, Output } from '@angular/core';
import { FormControl, FormGroup, NgControl, Validators } from '@angular/forms';
import { MessageService, SelectItem } from 'primeng/api';
import { Subject } from 'rxjs';
import { FormUtils } from '@irian-ro/util-forms';
import { ConfirmationDialogModel } from '@well/confirmation-dialog';
import { TranslateService } from '@ngx-translate/core';
import { EmailImportSettingsModel, LegalEntityModel } from '@well/common-libs/legal-entity-management/domain-model';
import { translationSchema } from '@well/translation';
import { constants } from '@well/infra/constants';
import { LegalEntityValidationService } from '@well/common-libs/legal-entity-management/data-access';

@Component({
  selector: 'well-legal-entity-inputs',
  templateUrl: './legal-entity-inputs.component.html',
  styleUrls: ['./legal-entity-inputs.component.scss'],
})
export class LegalEntityInputsComponent implements OnInit {
  get viewAddressFields(): boolean {
    return this._viewAddressFields;
  }

  @Input() set viewAddressFields(value: boolean) {
    this._viewAddressFields = value;
    if (value) {
      this.form?.get('officeAddressDTO')?.setValidators(Validators.required);
      this.form?.get('financialAddressDTO')?.setValidators(Validators.required);
    } else {
      this.form?.get('officeAddressDTO')?.setValidators([]);
      this.form?.get('financialAddressDTO')?.setValidators([]);
    }
    this.form?.updateValueAndValidity();
  }

  form: FormGroup | null = null;

  displayLegalEntityFields = false;

  @Input() isCompanyOwner = false;
  private _viewAddressFields = true;
  @Input() showEmailImportSettings = true;
  @Input() validateOnSelect = false;
  emailSettingsSuggestion = JSON.stringify(new EmailImportSettingsModel());

  @Input() legalEntitySearchLocation: 'ANAF' | 'ALL' = 'ALL';

  readonly autoOpenManual = input<boolean>(false);

  @Output() save: Subject<LegalEntityModel> = new Subject();
  @Output() validCompanySelected: Subject<LegalEntityModel | null> = new Subject();

  addManualCompany = false;

  uniqueIdNumberPattern = constants.regexConstants.uniqueIdNumberPattern;

  languages: SelectItem[] = [
    { label: 'Română', value: 'ro' },
    { label: 'English', value: 'en' }
  ];

  private _legalEntity: LegalEntityModel | null = null;
  @Input() set legalEntity(value: LegalEntityModel) {
    if (value) {
      this._legalEntity = value;

      if (!this._legalEntity.industryTypeValue) {
        this.form?.get('industryTypeValue')?.enable();
      }

      this.updateFormValues(value);
      if (this._legalEntity.uuid) {
        this.showLegalEntityFields();
      }
    }
  }

  get legalEntity(): LegalEntityModel | null {
    return this._legalEntity;
  }

  private selectedLegalEntity: LegalEntityModel | null = null;

  private confirmationDialogEnabled = true;
  private confirmationState = false;
  private dialogData: ConfirmationDialogModel | null = null;
  private dialogTranslations: any | undefined = undefined;
  messageService = inject(MessageService);

  constructor(
    private translateService: TranslateService,
    @Optional() private legalEntityValidationService: LegalEntityValidationService
  ) {
    this.form = this.createForm();
    this.form?.get('vatRegistration')?.disable();
    this.form?.get('vatSplitStatus')?.disable();
    this.form?.get('vatInactiveStatus')?.disable();
    this.form?.get('vatInactivationDate')?.disable();
  }

  get uniqueIdNumberFormControl() {
    return this.form?.get('uniqueIdNumber') as FormControl;
  }

  get vatRegistrationFormControl() {
    return this.form?.get('vatRegistration') as FormControl;
  }

  get vatSplitStatusFormControl() {
    return this.form?.get('vatSplitStatus') as FormControl;
  }

  get vatInactiveStatusFormControl() {
    return this.form?.get('vatInactiveStatus') as FormControl;
  }

  public onSubmit() {
    FormUtils.markAllAsTouched(this.form);
    FormUtils.markAllAsDirty(this.form);
    this.form?.updateValueAndValidity({ emitEvent: true });
    if (this.form?.disabled) {
      this.validCompanySelected.next(this.selectedLegalEntity);
    } else if (this.form?.valid) {
      this.save.next(this.form.getRawValue());
    }
  }

  ngOnInit() {
    this.getDialogTranslations();
    if (this._legalEntity?.validated && !this.isCompanyOwner) {
      this.form?.disable();
      this.showEmailImportSettings = false;
    }
    if (this.autoOpenManual()) {
      this.addNewCompany();
    }
  }

  onLegalEntitySelect(legalEntity: LegalEntityModel) {
    if (this.validateOnSelect) {
      this.legalEntityValidationService
        .exists(legalEntity.country ?? '', legalEntity.uniqueIdNumber ?? '')
        .subscribe((exists: any) => {
          if (exists) {
            this.messageService.add({
              key: 'global-error',
              severity: 'info',
              life: 5000,
              summary: this.translateService.instant('utils.existentCompany'),
              detail: this.translateService.instant('utils.existentCompanyDetails'),
            });
            return;
          } else {
            this.proceedWithSelection(legalEntity);
          }
        });
    } else {
      this.proceedWithSelection(legalEntity);
    }
  }

  private proceedWithSelection(legalEntity: LegalEntityModel) {
    this.selectedLegalEntity = legalEntity;
    this.showLegalEntityFields();
    this.form?.enable();
    if (legalEntity.validated) {
      this.form?.disable();
      this.showEmailImportSettings = false;
      this.updateFormValues(legalEntity);
      if (!legalEntity.officeAddressDTO || this.checkAddressRequiredFields(legalEntity.officeAddressDTO)) {
        this.officeAddressFormGroup?.enable();
        this.form?.updateValueAndValidity();
      }
      if (!legalEntity.financialAddressDTO || this.checkAddressRequiredFields(legalEntity.financialAddressDTO)) {
        this.financialAddressFormGroup?.enable();
        this.form?.updateValueAndValidity();
      }
    } else {
      if (legalEntity.origin === 'LOCAL') {
        this.enableAnafAutocompletedFields();
      } else {
        this.disableAnafAutocompletedFields();
      }
    }
    this.resetAllFields();
    this.updateFormValues(legalEntity);
  }

  checkAddressRequiredFields(obj: any): boolean {
    return ['country', 'postalCode', 'city'].some((key) => !obj[key]);
  }

  hideLegalEntityFields() {
    this.resetDialogState();
    this.addManualCompany = false;
    this.displayLegalEntityFields = false;
    this.enableAnafAutocompletedFields();
    this.resetAllFields();
  }

  addNewCompany() {
    this.confirmationDialogEnabled = false;
    this.showLegalEntityFields();
    this.form?.enable();
    this.addManualCompany = true;
  }

  getDumpDialogData() {
    this.dialogData = {
      enabled: this.confirmationDialogEnabled,
      dialogHeader: this.dialogTranslations.dialogHeader,
      dialogMessage: this.dialogTranslations.dialogMessage,
      acceptLabel: this.dialogTranslations.acceptLabel,
      cancelLabel: this.dialogTranslations.cancelLabel,
      confirmationState: this.confirmationState,
      acceptCallback: this.acceptDialog,
      cancelCallback: this.cancelDialog,
    };

    return this.dialogData;
  }

  private showLegalEntityFields() {
    this.displayLegalEntityFields = true;
  }

  get officeAddressFormGroup() {
    return this.form?.controls['officeAddressDTO'] as FormGroup;
  }

  get financialAddressFormGroup() {
    return this.form?.controls['financialAddressDTO'] as FormGroup;
  }

  private disableAnafAutocompletedFields() {
    this.uniqueIdNumberFormControl.disable();
  }

  private enableAnafAutocompletedFields() {
    this.uniqueIdNumberFormControl.enable();
  }

  private resetAllFields() {
    this.form?.reset({});
  }

  private updateFormValues(legalEntityModel: LegalEntityModel) {
    this.form?.patchValue(legalEntityModel);
    this.form?.updateValueAndValidity();
  }

  private createForm() {
    return new FormGroup({
      companyRegistrationUuid: new FormControl(null),
      legalEntityUuid: new FormControl(null),
      companyName: new FormControl(null, Validators.required),
      uniqueIdNumber: new FormControl(null, Validators.required),
      country: new FormControl(null, Validators.required),
      registrationCode: new FormControl(null),
      officeAddressDTO: new FormControl(null, Validators.required),
      financialAddressDTO: new FormControl(null, Validators.required),
      mainPhone: new FormControl(null),
      mainEmailAddress: new FormControl(null),
      industryTypeValue: new FormControl(null),
      preferredLanguage: new FormControl(null, Validators.required),
      vatRegistration: new FormControl(null),
      vatSplitStatus: new FormControl(null),
      vatInactiveStatus: new FormControl(null),
      vatInactivationDate: new FormControl(null),
      emailImportSettings: new FormControl(null),
      validated: new FormControl(null),
      uuid: new FormControl(null),
    });
  }

  private acceptDialog = (control: NgControl) => {
    this.confirmationState = true;
  };

  private cancelDialog = (control: NgControl) => {
    control?.valueAccessor?.writeValue(!control.value);
  };

  private getDialogTranslations() {
    this.translateService.stream('legalEntity.confirmationDialog').subscribe((res) => {
      this.dialogTranslations = res;
    });
  }

  private resetDialogState() {
    this.confirmationDialogEnabled = true;
    this.confirmationState = false;
  }

  protected readonly translationSchema = translationSchema;
}
